import { type JSX, useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { Link } from 'Components/Buttons/Buttons';
import { CollapsibleModal } from 'Components/Modal/CollapsibleModal/CollapsibleModal';
import { COMPLIANCE_REQUIREMENT_DETAILS } from 'Config/PathTemplates';
import { ComplianceDetails, MonthNumber } from 'Models/Dashboards';

export enum ComplianceModalType {
    MET = 'Met',
    NOT_ASSESSED = 'Not Assessed',
    UNMET = 'Unmet',
    NOT_MAPPED = 'Not Mapped',
}

export interface ComplianceRequirementDisplayModalProps {
    regulationInformation: ComplianceRequirementDisplayInformation;
    hideModal: () => void;
    api: DashboardApi;
}

/**
 * @param timeFrame The time frame for which to load a snapshot of compliance details. If not provided, current details will be loaded.
 * @param targetMaturity The target maturity for which to load a snapshot of compliance details. Applies only to FFIEC CAT.
 */
export interface ComplianceRequirementDisplayInformation {
    regulationName: string;
    regulationTypeSelected: ComplianceModalType;
    timeFrame?: { month: MonthNumber; year: number };
    targetMaturity?: string;
}

export const ComplianceRequirementDisplayModal = (props: ComplianceRequirementDisplayModalProps): JSX.Element => {
    const [complianceHistoryDetails, setComplianceHistoryDetails] = useState<ComplianceDetails>();
    const [errorMessage, setErrorMessage] = useState<string>();

    useEffect(() => {
        const getComplianceHistoryDetails = async () => {
            try {
                if (props.regulationInformation.timeFrame) {
                    if (props.regulationInformation.targetMaturity) {
                        const historyDetails = await props.api.getComplianceHistoryDetailsFFIECCAT(props.regulationInformation.regulationName, props.regulationInformation.timeFrame.month, props.regulationInformation.timeFrame.year, props.regulationInformation.targetMaturity);
                        setComplianceHistoryDetails(historyDetails.data.details);
                    } else {
                        const historyDetails = await props.api.getComplianceHistoryDetails(props.regulationInformation.regulationName, props.regulationInformation.timeFrame.month, props.regulationInformation.timeFrame.year);
                        setComplianceHistoryDetails(historyDetails.data.details);
                    }
                } else {
                    const historyDetails = await props.api.getCurrentComplianceDetails(props.regulationInformation.regulationName);
                    setComplianceHistoryDetails(historyDetails.data);
                }
            } catch (err) {
                setErrorMessage(err.message);
            }
        };
        getComplianceHistoryDetails();
    }, [props.api, props.regulationInformation]);

    const createListing = (requirements: string[]) => {
        return requirements.sort((requirementA, requirementB) => requirementA.localeCompare(requirementB, undefined, { numeric: true, sensitivity: 'base' })).map((requirement) => <Link openInNewTab key={`${props.regulationInformation.regulationName} ${requirement}`} size="sm" to={generatePath(COMPLIANCE_REQUIREMENT_DETAILS, { regulationName: props.regulationInformation.regulationName, requirement: requirement })}>{`${props.regulationInformation.regulationName} ${requirement}`}</Link>);
    };

    const createRegulationListing = (details: ComplianceDetails) => {
        if (props.regulationInformation.regulationTypeSelected === ComplianceModalType.UNMET) {
            return createListing(details.not_met);
        } else if (props.regulationInformation.regulationTypeSelected === ComplianceModalType.MET) {
            return createListing(details.met);
        } else if (props.regulationInformation.regulationTypeSelected === ComplianceModalType.NOT_ASSESSED) {
            return createListing(details.awaiting_assessments);
        } else {
            return createListing(details.not_mapped);
        }
    };

    const headerText = { text: 'Compliance Requirements', secondaryText: `${props.regulationInformation.regulationName} | ${props.regulationInformation.regulationTypeSelected.toUpperCase()}` };

    if (errorMessage) {
        return <CollapsibleModal isLoading failureMessage={errorMessage} headerText={headerText} hideModal={props.hideModal} />;
    }

    if (!complianceHistoryDetails) {
        return <CollapsibleModal isLoading headerText={headerText} hideModal={props.hideModal} />;
    }

    return <CollapsibleModal displayItems={createRegulationListing(complianceHistoryDetails)} headerText={headerText} hideModal={props.hideModal} />;
};
